import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type AddressValue = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
};

export const EMPTY_ADDRESS: AddressValue = { line1: "", line2: "", city: "", state: "", zip: "" };

export function toAddress(value: unknown): AddressValue {
  const v = (value ?? {}) as Partial<AddressValue>;
  return {
    line1: v.line1 ?? "",
    line2: v.line2 ?? "",
    city: v.city ?? "",
    state: v.state ?? "",
    zip: v.zip ?? "",
  };
}

export function addressIsComplete(a: AddressValue) {
  return Boolean(a.line1.trim() && a.city.trim() && a.state.trim() && a.zip.trim());
}

/** Patterns the state will not accept as a street address. */
const BLOCKED = [
  /\bp\.?\s*o\.?\s*box\b/i,
  /\bpob\b/i,
  /\bbox\s*#?\s*\d+/i,
  /\bups\s*store\b/i,
  /\bups\b/i,
  /anytime\s*mailbox/i,
  /\bregus\b/i,
  /ipostal/i,
];

export function addressLooksLikeMailbox(a: AddressValue) {
  const line = `${a.line1} ${a.line2}`;
  return BLOCKED.some((re) => re.test(line));
}

export function AddressFields({
  value,
  onChange,
  idPrefix,
}: {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  idPrefix: string;
}) {
  const set = (key: keyof AddressValue) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [key]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-line1`}>Street address</Label>
        <Input id={`${idPrefix}-line1`} value={value.line1} onChange={set("line1")} className="min-h-11" />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-line2`}>Apartment or unit (optional)</Label>
        <Input id={`${idPrefix}-line2`} value={value.line2} onChange={set("line2")} className="min-h-11" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-2 sm:col-span-1">
          <Label htmlFor={`${idPrefix}-city`}>City</Label>
          <Input id={`${idPrefix}-city`} value={value.city} onChange={set("city")} className="min-h-11" />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-state`}>State</Label>
          <Input id={`${idPrefix}-state`} value={value.state} onChange={set("state")} className="min-h-11" />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-zip`}>ZIP</Label>
          <Input id={`${idPrefix}-zip`} value={value.zip} onChange={set("zip")} className="min-h-11" />
        </div>
      </div>
    </div>
  );
}
